const hygienX300 = {
  slug: 'hygienx-300',
  name: 'HygienX 300',
  testid: 'hygienx',
  accent: 'emerald',
  breadcrumb: { label: 'Floor Cleaning Robots', href: '/floor-cleaning-robots' },
  brochureUrl:
    'https://customer-assets.emergentagent.com/job_80fffe0f-7ad4-44ce-aa36-08a4bccc4edb/artifacts/xq6h1yuo_Floor%20Cleaning%20Robot%20HygienX%20300.pdf',
  meta: {
    title: 'HygienX 300 - AI-Powered Robotic Vacuum Cleaner | Harpalyk',
    description:
      'HygienX 300: Fully automated smart cleaning robot with LiDAR navigation, self-cleaning dock, automatic mop washing, hot-air drying and up to 15,000Pa suction. Premium AI-powered hands-free home cleaning.',
    keywords:
      'HygienX 300, robotic vacuum cleaner, AI robot vacuum, self-cleaning robot, automatic mop washing, LiDAR navigation, Harpalyk, smart home cleaning robot India',
    canonical: 'https://harpalyk.com/products/hygienx-300',
  },
  schema: {
    name: 'Harpalyk HygienX 300',
    description:
      'All-in-One Fully Automatic Premium AI-Powered Hands-free Home Cleaning Robotic Vacuum Cleaner with LiDAR navigation, self-cleaning dock, automatic mop washing, hot-air drying and up to 15,000Pa suction.',
    category: 'Robotic Vacuum Cleaner',
    price: '79990',
    ratingValue: '4.9',
    reviewCount: '156',
  },
  hero: {
    gradient: 'from-gray-900 via-gray-800 to-emerald-900',
    badge: 'SMARTBOT · Premium AI-Powered Home Cleaning',
    tagline: 'Smart Cleaning. Powerful Performance.',
    description:
      'All-in-One Fully Automatic Premium AI-Powered Hands-free Home Cleaning Robotic Vacuum Cleaner. Experience intelligent, powerful, and fully automated cleaning for modern luxury homes.',
    highlights: [
      { label: '15,000Pa Suction', icon: 'Zap' },
      { label: 'LiDAR + AI Navigation', icon: 'Navigation' },
      { label: 'Auto Mop Washing', icon: 'Droplets' },
      { label: 'Hot Air Drying', icon: 'Wind' },
    ],
    galleryAccent: 'emerald',
    galleryBadge: 'Premium Model',
  },
  overview: {
    heading: 'Advanced Cleaning Technology for a Healthier Home',
    sub: 'The HygienX 300 delivers an uninterrupted, maintenance-free, and truly intelligent cleaning experience. Designed for users who demand intelligent automation, superior hygiene, luxury convenience, and effortless maintenance.',
    checklistTitle: 'Intelligent, Powerful & Fully Automated',
    checklist: [
      'Self-Navigating with High Precision LiDAR + DTOF + Adaptive Avoidance',
      'Automatic Mop Washing with 10N High-Pressure Scrubbing',
      'Hot Air Drying at 55°C for hygienic, odor-free mops',
      'Self-Cleaning Dock handles all maintenance automatically',
      'Multi-Floor Mapping supports up to 5 independent floor maps',
      'Automatic Detergent Mixing for enhanced mopping performance',
      'Up to 180 minutes runtime covering 3000 sq.ft. per charge',
    ],
    panelGradient: 'from-emerald-50 to-gray-50',
    stats: [
      { value: '15,000Pa', label: 'Max Suction' },
      { value: '180 min', label: 'Runtime' },
      { value: '3000 sq.ft', label: 'Coverage' },
      { value: '5 Maps', label: 'Multi-Floor' },
      { value: '55°C', label: 'Hot Air Dry' },
      { value: '10N', label: 'Mop Pressure' },
    ],
  },
  features: {
    heading: '10 Powerful Features for Effortless Cleaning',
    items: [
      { icon: 'Zap', title: '15,000Pa Suction', description: 'Ultra-powerful suction removes embedded dirt, pet hair, and fine dust with ease' },
      { icon: 'Navigation', title: 'LiDAR Navigation', description: 'High precision LiDAR + DTOF for intelligent room recognition and route optimization' },
      { icon: 'Cpu', title: 'RT²R 4.0 Mapping', description: 'Ultra-precise 360° environmental scan maps your entire home within minutes' },
      { icon: 'Droplets', title: 'Auto Mop Washing', description: 'Self-cleaning mops with scrubbing technology for hygienic, hands-free operation' },
      { icon: 'ThermometerSun', title: 'Hot Air Drying (55°C)', description: 'Eliminates moisture, odors, and bacterial buildup from mop pads' },
      { icon: 'Home', title: 'Self-Cleaning Dock', description: 'All-in-one base station handles washing, drying, and dust disposal automatically' },
      { icon: 'Sparkles', title: 'Auto Detergent Dispensing', description: 'Smart dispenser with one bottle supporting up to 180 auto-wash cycles' },
      { icon: 'RotateCcw', title: 'Anti-Tangle Brushes', description: 'Dual cyclone roller brushes with 45° angled bristles prevent hair wrapping' },
      { icon: 'Filter', title: 'H12 HEPA Filtration', description: '3-layer antibacterial filtration captures 99.97% of particles for healthier air' },
      { icon: 'Maximize2', title: 'Multi-Floor Mapping', description: 'Stores up to 5 independent floor maps for multi-story homes' },
    ],
  },
  sections: [
    {
      type: 'split',
      label: 'Navigation Technology',
      heading: 'AI-Powered Smart Navigation',
      desc: 'Experience faster mapping, smarter cleaning, and better coverage with our advanced RT²R 4.0 Mapping Technology.',
      itemIcon: 'Navigation',
      items: [
        { title: 'High Precision LiDAR + DTOF', desc: 'Ultra-precise 360° environmental scan for accurate navigation' },
        { title: '4x Higher Object Detection', desc: 'Advanced adaptive avoidance prevents collisions and drops' },
        { title: 'Intelligent Room Recognition', desc: 'Automatically identifies and labels different rooms' },
        { title: 'Route Optimization', desc: 'AI calculates the most efficient cleaning path' },
        { title: 'No-Go Zones & Virtual Walls', desc: 'Customizable boundaries for complete control' },
      ],
      panel: {
        gradient: 'from-emerald-600 to-emerald-800',
        title: 'Multi-Floor Smart Mapping',
        desc: 'The HygienX 300 supports up to 5 independent floor maps, making it perfect for multi-story homes. Simply carry the robot to a new floor—it automatically recognizes and loads the correct map.',
        subClass: 'text-emerald-100',
        statClass: 'text-emerald-300',
        stats: [
          { value: '5', label: 'Floor Maps' },
          { value: '360°', label: 'LiDAR Scan' },
        ],
      },
    },
    {
      type: 'darkCards',
      label: 'Cleaning Performance',
      heading: 'Exceptional Deep-Cleaning Power',
      sub: 'With 15,000Pa suction and AI-powered 10N high-pressure scrubbing, the HygienX 300 effectively removes stubborn stains, sticky messes, and deep floor dirt.',
      cards: [
        { icon: 'Zap', title: 'Ultra-Powerful Suction', desc: 'Japanese BLDC brushless motor delivers 8,000 to 15,000Pa adjustable suction power. Eradicates hidden debris, removes embedded carpet dust, and captures large particles effortlessly.', badge: '15,000 Pa Max' },
        { icon: 'Droplets', title: 'Smart Mopping System', desc: 'Dual rotating mops spin at 185 RPM with 10N pressure, delivering deep scrubbing power. Consistent water flow ensures streak-free, exceptionally clean floors.', badge: '10N Pressure • 185 RPM' },
        { icon: 'Filter', title: 'H12 HEPA Filtration', desc: 'Premium 3-layer antibacterial filtration system captures 99.97% of particles including dust mites, pollen, and pet dander for healthier indoor air quality.', badge: '99.97% Capture Rate' },
      ],
    },
    {
      type: 'duoPanels',
      panels: [
        {
          theme: 'blue',
          icon: 'Waves',
          title: 'Intelligent Carpet Auto-Lift',
          desc: 'The HygienX 300 features adaptive detection that automatically lifts mop pads by 10mm when transitioning to carpeted surfaces—preventing moisture transfer and carpet staining while maintaining seamless cleaning between floor types.',
          list: ['Automatic 10mm mop lifting', 'Adaptive carpet detection', 'Prevents moisture damage', 'Seamless floor transitions'],
        },
        {
          theme: 'amber',
          icon: 'PawPrint',
          title: 'Perfect for Pet Owners',
          desc: 'Advanced anti-tangle dual-drive roller brush system with 45° angled bristles and widened soft rubber blades minimizes hair wrapping and clogging. Combined with powerful suction, it captures pet hair, fur, and dander with ease.',
          list: ['Anti-tangle dual cyclone brushes', '45° angled bristle design', 'Captures pet hair effortlessly', 'HEPA filtration for allergens'],
        },
      ],
    },
    {
      type: 'iconCards',
      bg: 'gray',
      label: 'Self-Maintenance',
      heading: 'All-in-One Self-Cleaning Dock',
      sub: 'True hands-free experience—from cleaning to maintenance, everything is done for you automatically.',
      cards: [
        { icon: 'Droplets', title: 'Auto Mop Washing', desc: 'Scrubbing-style mop cleaning with automatic detergent dispensing' },
        { icon: 'ThermometerSun', title: 'Hot Air Drying', desc: '55°C hot air eliminates moisture, odors, and bacterial buildup' },
        { icon: 'Trash2', title: 'Hands-Free Emptying', desc: '1000ml compressed dustbin with bagless auto-compression' },
        { icon: 'Sparkles', title: 'Always Ready', desc: 'Stay dry, fresh, and ready for the next clean 24/7' },
      ],
      bigStats: [
        { value: '4L + 4L', label: 'Clean & Dirty Water Tanks' },
        { value: '180', label: 'Auto-Wash Cycles Per Bottle' },
        { value: '20 Days', label: 'Hands-Free Operation' },
      ],
    },
    {
      type: 'split',
      reverse: true,
      label: 'Smart Control',
      heading: 'Control From Anywhere',
      desc: 'The HygienX 300 integrates seamlessly with your smart home ecosystem. Use the app, push buttons, or voice commands to control your robot.',
      itemIcon: 'Wifi',
      items: [
        { title: 'App Control', desc: 'Full control through Smart Life/Tuya app' },
        { title: 'Push Buttons', desc: 'Quick access controls on the robot and dock' },
        { title: 'Voice Commands', desc: 'Works with popular smart home platforms' },
      ],
      panel: {
        gradient: 'from-purple-600 to-indigo-700',
        icon: 'Smartphone',
        title: 'Smart Life / Tuya App',
        desc: 'Control, schedule, and customize cleaning anytime and anywhere with our intuitive smart app.',
        subClass: 'text-purple-100',
        chips: [
          'Real-time navigation view',
          'Multi-floor map management',
          'Custom cleaning schedules',
          'No-go zones & virtual walls',
          'Cleaning history & reports',
          'Voice command support',
        ],
        chipIcon: 'CheckCircle',
        chipIconClass: 'text-purple-300',
      },
    },
  ],
  specs: {
    defaultTab: 'cleaning',
    tabs: [
      { id: 'cleaning', label: 'Cleaning' },
      { id: 'capacity', label: 'Capacity' },
      { id: 'dimensions', label: 'Dimensions' },
    ],
    data: {
      cleaning: [
        { label: 'Suction Power', value: '8,000 ~ 15,000 Pa' },
        { label: 'Mopping Pressure', value: '10N High-Pressure' },
        { label: 'Mop Rotation Speed', value: '185 RPM' },
        { label: 'Cleaning Coverage', value: 'Up to 3000 sq.ft.' },
        { label: 'Mop Auto-Lift Height', value: '10mm' },
        { label: 'Obstacle Crossing', value: '≤ 20mm' },
      ],
      capacity: [
        { label: 'Dustbin Capacity', value: '1000ml (Compressed)' },
        { label: 'Clean Water Tank', value: '4L' },
        { label: 'Dirty Water Tank', value: '4L' },
        { label: 'Battery Capacity', value: '5200 mAh EV-Grade' },
        { label: 'Runtime', value: 'Up to 180 minutes' },
        { label: 'Multi-Floor Maps', value: 'Up to 5 maps' },
      ],
      dimensions: [
        { label: 'Robot Size', value: '35 × 35 × 10.5 cm' },
        { label: 'Dock Size', value: '42.1 × 39.8 × 39.9 cm' },
        { label: 'Motor Type', value: 'Japanese BLDC Brushless' },
        { label: 'Filtration', value: 'H12 HEPA (3-layer)' },
        { label: 'Hot Air Drying', value: '55°C' },
        { label: 'Warranty', value: '1 Year Standard' },
      ],
    },
  },
  contact: {
    heading: 'Ready for Effortless, Intelligent Cleaning?',
    sub: 'Upgrade your home cleaning to a new level of intelligence and convenience with the HygienX 300. Contact us today for pricing and availability.',
    gradient: 'from-emerald-600 to-emerald-800',
  },
  faqs: [
    {
      question: 'What makes HygienX 300 different from other robot vacuums?',
      answer:
        "The HygienX 300 combines 15,000Pa suction power with AI-powered 10N high-pressure scrubbing, automatic mop washing, hot air drying at 55°C, and a fully automated self-cleaning dock. It's designed for hands-free operation with RT²R 4.0 mapping technology and premium H12 HEPA filtration—delivering an uninterrupted, maintenance-free cleaning experience for modern luxury homes.",
    },
    {
      question: 'How does the self-cleaning dock work?',
      answer:
        'The all-in-one docking station features automatic mop washing with scrubbing technology, hot air drying at 55°C to eliminate moisture and bacteria, automatic detergent dispensing (one bottle supports 180 wash cycles), and hands-free dust compression into a large 1000ml bagless dustbin. It handles all maintenance automatically—you just need to empty the dirty water tank and refill the clean water tank periodically.',
    },
    {
      question: 'Is the HygienX 300 suitable for homes with pets?',
      answer:
        'Absolutely! The HygienX 300 features an advanced anti-tangle dual-drive roller brush system with 45° angled bristles and widened soft rubber blades specifically designed for pet hair and long hair. The powerful 15,000Pa suction captures pet fur, dander, and debris while the H12 HEPA filtration traps allergens for healthier indoor air.',
    },
    {
      question: 'Can it handle both hard floors and carpets?',
      answer:
        'Yes! The intelligent carpet detection system automatically lifts the mop pads by 10mm when transitioning from hard floors to carpets, preventing moisture transfer and carpet staining. It seamlessly switches between vacuuming and mopping modes based on floor type, ensuring optimal cleaning on marble, tiles, hardwood, and carpeted surfaces.',
    },
    {
      question: 'What app features are available for the HygienX 300?',
      answer:
        'The HygienX 300 connects to the Smart Life/Tuya app, allowing you to control, schedule, and customize cleaning from anywhere. Features include multi-floor mapping, no-go zones, virtual walls, cleaning history, real-time navigation view, customized room cleaning, and voice command support through smart home integration.',
    },
  ],
  related: [
    { label: 'All Floor Robots', href: '/floor-cleaning-robots' },
    { label: 'Pool Cleaning Robots', href: '/pool-cleaning-robots' },
    { label: 'Window Cleaning Robots', href: '/window-cleaning-robots' },
    { label: 'Robotic Lawn Mowers', href: '/robotic-lawn-mowers' },
    { label: 'View All Products', href: '/#products' },
  ],
};

export default hygienX300;
